import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { User, History, Layers, ArrowLeft, Copy } from 'lucide-react';
import {
  getAuditEntry,
  getAuditTransaction,
  type AuditEntryDetail,
  type AuditTransaction,
} from '@/api/v2-activity';
import {
  actionLabel,
  actionBadgeClass,
  entityTypeLabel,
  actorTypeLabel,
  originLabel,
  formatDateTime,
  formatDiffValue,
} from './labels';
import { cn } from '@/lib/utils';

interface AuditEntrySheetProps {
  schoolCode: string;
  entryId: string | null;
  /** Nom humain de l'objet (fourni par l'enrichissement de la liste) */
  entityLabel?: string | null;
  onClose: () => void;
  /** Filtre le tableau sur l'activité de cet utilisateur */
  onFilterUser: (userId: string) => void;
  /** Filtre le tableau sur l'historique de cet objet */
  onFilterEntity: (entityType: string, entityId: string) => void;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-gray-500 dark:text-slate-400 shrink-0">{label}</span>
      <span className="text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">{title}</p>
      <pre className="text-xs bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-md p-3 overflow-x-auto max-h-64">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/**
 * Drawer de détail d'une entrée d'audit V2 : lecture support (diff lisible,
 * utilisateur, contexte) + « Vue développeur » (JSON brut, snapshots, ids).
 * Permet de remonter à l'opération complète (transaction) et de rebondir sur
 * l'historique de l'objet ou l'activité de l'utilisateur.
 */
export function AuditEntrySheet({
  schoolCode,
  entryId,
  entityLabel,
  onClose,
  onFilterUser,
  onFilterEntity,
}: AuditEntrySheetProps) {
  const [detail, setDetail] = useState<AuditEntryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [transaction, setTransaction] = useState<AuditTransaction | null>(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entryId) {
      setDetail(null);
      setTransaction(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setTransaction(null);
        const data = await getAuditEntry(schoolCode, entryId);
        if (!cancelled) setDetail(data);
      } catch {
        if (!cancelled) setError("Impossible de charger le détail de l'événement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolCode, entryId]);

  const loadTransaction = async () => {
    const transactionId = detail?.context?.transactionId;
    if (!transactionId) return;
    try {
      setTransactionLoading(true);
      const data = await getAuditTransaction(schoolCode, transactionId);
      setTransaction(data);
    } catch {
      setError("Impossible de charger l'opération complète");
    } finally {
      setTransactionLoading(false);
    }
  };

  const actor = detail?.actor;
  const context = detail?.context;

  return (
    <Sheet open={entryId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:w-[560px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 pr-8">
            {detail && (
              <>
                <Badge className={cn('border-transparent', actionBadgeClass(detail.action))}>
                  {actionLabel(detail.action)}
                </Badge>
                <span>
                  {entityTypeLabel(detail.entityType)}
                  {entityLabel ? ` — ${entityLabel}` : ''}
                </span>
              </>
            )}
            {!detail && 'Détail de l\'événement'}
          </SheetTitle>
          {detail && (
            <SheetDescription>{formatDateTime(detail.occurredAt)}</SheetDescription>
          )}
        </SheetHeader>

        <SheetBody className="space-y-6">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {error && !loading && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {detail && !loading && !transaction && (
            <>
              {/* Utilisateur */}
              <section>
                <h4 className="text-sm font-semibold flex items-center gap-1.5 mb-1">
                  <User className="h-4 w-4 text-gray-400" />
                  Utilisateur
                </h4>
                <div className="rounded-lg border border-gray-100 dark:border-slate-700 px-4 py-2 divide-y divide-gray-50 dark:divide-slate-800">
                  <InfoRow label="Nom" value={actor?.label ?? 'Système'} />
                  <InfoRow label="Type" value={actorTypeLabel(actor?.type ?? 'SYSTEM')} />
                  {actor?.role && <InfoRow label="Rôle" value={actor.role} />}
                </div>
                {actor?.id && (
                  <Button
                    variant="link"
                    size="sm"
                    className="px-0 h-7"
                    onClick={() => onFilterUser(actor.id!)}
                  >
                    <History className="h-3.5 w-3.5 mr-1" />
                    Voir toute l'activité de cet utilisateur
                  </Button>
                )}
              </section>

              {/* Modifications */}
              <section>
                <h4 className="text-sm font-semibold mb-1">
                  Modifications
                  {detail.diff.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {detail.diff.length} champ{detail.diff.length > 1 ? 's' : ''}
                    </span>
                  )}
                </h4>
                {detail.diff.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Aucun détail de champ pour cette action.
                  </p>
                ) : (
                  <div className="rounded-lg border border-gray-100 dark:border-slate-700 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-800 text-left text-xs text-gray-500 dark:text-slate-400">
                          <th className="px-3 py-2 font-medium">Champ</th>
                          <th className="px-3 py-2 font-medium">Avant</th>
                          <th className="px-3 py-2 font-medium">Après</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                        {detail.diff.map((d) => (
                          <tr key={d.field}>
                            <td className="px-3 py-2 font-medium">{d.label ?? d.field}</td>
                            <td className="px-3 py-2 text-red-700 dark:text-red-400 break-all">
                              {formatDiffValue(d.before)}
                            </td>
                            <td className="px-3 py-2 text-green-700 dark:text-green-400 break-all">
                              {formatDiffValue(d.after)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Objet concerné */}
              <section>
                <h4 className="text-sm font-semibold mb-1">Objet concerné</h4>
                <div className="rounded-lg border border-gray-100 dark:border-slate-700 px-4 py-2 divide-y divide-gray-50 dark:divide-slate-800">
                  <InfoRow label="Type" value={entityTypeLabel(detail.entityType)} />
                  <InfoRow
                    label="Identifiant"
                    value={<code className="text-xs">{detail.entityId}</code>}
                  />
                </div>
                <Button
                  variant="link"
                  size="sm"
                  className="px-0 h-7"
                  onClick={() => onFilterEntity(detail.entityType, detail.entityId)}
                >
                  <History className="h-3.5 w-3.5 mr-1" />
                  Voir l'historique de cet objet
                </Button>
              </section>

              {/* Contexte */}
              <section>
                <h4 className="text-sm font-semibold mb-1">Contexte</h4>
                <div className="rounded-lg border border-gray-100 dark:border-slate-700 px-4 py-2 divide-y divide-gray-50 dark:divide-slate-800">
                  <InfoRow label="Origine" value={originLabel(context?.origin ?? 'HTTP')} />
                  {context?.ipAddress && <InfoRow label="Adresse IP" value={context.ipAddress} />}
                  {context?.httpMethod && context?.route && (
                    <InfoRow
                      label="Requête"
                      value={
                        <code className="text-xs">
                          {context.httpMethod} {context.route}
                        </code>
                      }
                    />
                  )}
                </div>
              </section>

              {/* Opération groupée */}
              {context?.transactionId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadTransaction}
                  disabled={transactionLoading}
                >
                  <Layers className="h-4 w-4 mr-1.5" />
                  {transactionLoading ? 'Chargement…' : "Voir l'opération complète"}
                </Button>
              )}

              {/* Vue développeur */}
              <section className="border-t border-gray-100 dark:border-slate-700 pt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Switch id="audit-dev-mode" checked={devMode} onCheckedChange={setDevMode} />
                  <Label htmlFor="audit-dev-mode" className="text-sm cursor-pointer">
                    Vue développeur
                  </Label>
                </div>
                {devMode && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-gray-100 dark:border-slate-700 px-4 py-2 divide-y divide-gray-50 dark:divide-slate-800">
                      <InfoRow label="Entry ID" value={<code className="text-xs">{detail.id}</code>} />
                      <InfoRow
                        label="Transaction ID"
                        value={<code className="text-xs">{context?.transactionId ?? '—'}</code>}
                      />
                      <InfoRow
                        label="Request ID"
                        value={<code className="text-xs">{context?.requestId ?? '—'}</code>}
                      />
                      {context?.userAgent && <InfoRow label="User-Agent" value={context.userAgent} />}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(JSON.stringify(detail, null, 2))}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Copier le JSON complet
                    </Button>
                    {detail.snapshotBefore && (
                      <JsonBlock title="Snapshot avant" value={detail.snapshotBefore} />
                    )}
                    {detail.snapshotAfter && (
                      <JsonBlock title="Snapshot après" value={detail.snapshotAfter} />
                    )}
                    {detail.metadata && Object.keys(detail.metadata).length > 0 && (
                      <JsonBlock title="Métadonnées" value={detail.metadata} />
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {/* Vue transaction : toutes les entrées de la même opération */}
          {transaction && !loading && (
            <>
              <Button variant="ghost" size="sm" className="px-0" onClick={() => setTransaction(null)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Retour au détail
              </Button>
              <section>
                <h4 className="text-sm font-semibold mb-1">
                  Opération du {formatDateTime(transaction.startedAt)}
                </h4>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-3">
                  {transaction.entries.length} événement{transaction.entries.length > 1 ? 's' : ''} —{' '}
                  {transaction.actor?.label ?? 'Système'} ({originLabel(transaction.context?.origin ?? 'HTTP')})
                </p>
                <div className="space-y-2">
                  {transaction.entries.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => {
                        setDetail(entry);
                        setTransaction(null);
                      }}
                      className="w-full text-left rounded-lg border border-gray-100 dark:border-slate-700 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Badge className={cn('border-transparent', actionBadgeClass(entry.action))}>
                          {actionLabel(entry.action)}
                        </Badge>
                        <span className="text-sm font-medium">{entityTypeLabel(entry.entityType)}</span>
                        <span className="ml-auto text-xs text-gray-400">
                          {entry.diff.length > 0 &&
                            `${entry.diff.length} champ${entry.diff.length > 1 ? 's' : ''}`}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
